import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Clock, Car, Calendar } from 'lucide-react';
import '../styles/reservasStyle.css';
import parkingService from '../services/parkingService';
import reservationService from '../services/reservationsService';
import vehicleService from '../services/vehicleService';
import authService from '../services/loginService';

const ReservaParqueo = () => {
  // Estados básicos
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [entryTime, setEntryTime] = useState('');
  const [exitTime, setExitTime] = useState('');
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedParkingId, setSelectedParkingId] = useState('');
  const [currentParking, setCurrentParking] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const imageRef = useRef(null);

  // Estados para datos del backend
  const [registeredParkings, setRegisteredParkings] = useState([]);
  const [userVehicles, setUserVehicles] = useState([]); // Asegurar que sea array desde el inicio

  // Obtener usuario usando el servicio de autenticación
  const [user] = useState(() => authService.getCurrentUser());

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Efectos de inicialización
  useEffect(() => {
    console.log('Usuario actual:', user);
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedParkingId && registeredParkings.length > 0) {
      const parking = registeredParkings.find(p => p._id === selectedParkingId);
      setCurrentParking(parking || null);
      setSelectedSpot(null);
    }
  }, [selectedParkingId, registeredParkings]);

  // ========== FUNCIONES DE CARGA DE DATOS ==========

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadParkings(),
        loadUserVehicles()
      ]);
    } catch (error) {
      console.error('Error al cargar datos iniciales:', error);
      alert('Error al cargar los datos. Por favor recarga la página.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadParkings = async () => {
    try {
      const response = await parkingService.getAllParkings();
      setRegisteredParkings(response.parkings || response || []);
    } catch (error) {
      console.error('Error al cargar parqueos:', error);
      throw error;
    }
  };

  const loadUserVehicles = async () => {
    try {
      if (!user?.idCard) {
        console.warn('No hay usuario logueado');
        return;
      }

      // Cargar vehículos del usuario desde el backend
      const response = await vehicleService.getUserVehicles(user.idCard);
      console.log('Vehículos obtenidos del backend:', response);

      // El backend devuelve { vehicles: [array_of_plates] }
      const vehiclePlates = response?.vehicles || [];

      // Asegurar que sea un array
      const validVehicles = Array.isArray(vehiclePlates) ? vehiclePlates : [];
      setUserVehicles(validVehicles);

      // Seleccionar el primer vehículo por defecto
      if (validVehicles.length > 0) {
        setSelectedCar(validVehicles[0]);
      }
    } catch (error) {
      console.error('Error al cargar vehículos:', error);
      // Usar vehículos por defecto en caso de error
      const defaultVehicles = ['MLS 135', 'ABC 123', 'XYZ 789'];
      setUserVehicles(defaultVehicles);
      setSelectedCar(defaultVehicles[0]);
    }
  };

  // ========== FUNCIONES DE MANEJO DE SPOTS ==========

  const toggleSpotSelection = (spotId) => {
    const spot = currentParking.spots.find(s => s.id === spotId);
    if (!spot || spot.status === 'occupied' || spot.status === 'reserved') return;

    // Solo permitir seleccionar un spot a la vez
    if (selectedSpot === spotId) {
      setSelectedSpot(null); // Deseleccionar si ya está seleccionado
    } else {
      setSelectedSpot(spotId); // Seleccionar nuevo spot
    }
  };

  const getSpotClass = (spot) => {
    const baseClass = 'reservas-parking-spot';
    if (selectedSpot === spot.id) {
      return baseClass + ' spot-selected';
    }
    switch (spot.status) {
      case 'available':
        return baseClass + ' spot-available';
      case 'occupied':
        return baseClass + ' spot-occupied';
      case 'reserved':
        return baseClass + ' spot-reserved';
      case 'maintenance':
        return baseClass + ' spot-maintenance';
      default:
        return baseClass + ' spot-available';
    }
  };

  // ========== FUNCIONES DE CALENDARIO ==========

  const changeMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    // Resetear al primer día del mes
    newDate.setDate(1);
    setSelectedDate(newDate);
  };

  const selectDay = (day) => {
    if (!day) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(day);
    setSelectedDate(newDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Días vacíos al inicio (ajustar para que lunes sea 0)
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(null);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // ========== VALIDACIONES ==========

  const validateReservationData = () => {
    if (!user?.idCard) {
      alert('Error: No se pudo identificar al usuario. Por favor, inicie sesión nuevamente.');
      return false;
    }

    if (!selectedParkingId) {
      alert('Por favor seleccione un parqueo.');
      return false;
    }

    if (!selectedSpot) {
      alert('Por favor seleccione un espacio de parqueo.');
      return false;
    }

    if (!selectedCar) {
      alert('Por favor seleccione un vehículo.');
      return false;
    }

    if (!entryTime) {
      alert('Por favor ingrese la hora de entrada.');
      return false;
    }

    if (!exitTime) {
      alert('Por favor ingrese la hora de salida.');
      return false;
    }

    // Validar que la hora de salida sea posterior a la de entrada
    if (entryTime >= exitTime) {
      alert('La hora de salida debe ser posterior a la hora de entrada.');
      return false;
    }

    // Validar que la fecha sea hoy o futura
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateCopy = new Date(selectedDate);
    selectedDateCopy.setHours(0, 0, 0, 0);

    if (selectedDateCopy < today) {
      alert('No se pueden hacer reservas para fechas pasadas.');
      return false;
    }

    return true;
  };

  // ========== FUNCIÓN PRINCIPAL DE CREACIÓN DE RESERVA ==========

  const handleCreateReservation = async () => {
    if (!validateReservationData()) {
      return;
    }

    try {
      setIsLoading(true);

      const reservationData = {
        idCard: user.idCard,
        plate: selectedCar,
        idParking: selectedParkingId,
        idSpot: selectedSpot,
        dateIn: selectedDate.toISOString().split('T')[0], // YYYY-MM-DD
        timeIn: entryTime, // HH:MM
        timeOut: exitTime, // HH:MM
        status: 'Pendiente'
      };

      console.log('Enviando datos de reserva:', reservationData);

      const response = await reservationService.createReservation(reservationData);

      if (response && response.message) {
        alert('¡Reserva creada exitosamente!');

        // Recargar los datos del parqueo para actualizar los spots
        await loadParkings();

        // Limpiar el formulario
        resetForm();
      } else {
        throw new Error('Respuesta inesperada del servidor');
      }

    } catch (error) {
      console.error('Error al crear reserva:', error);

      let errorMessage = 'Error al crear la reserva. ';

      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Por favor intente de nuevo.';
      }

      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FUNCIONES AUXILIARES ==========

  const resetForm = () => {
    setSelectedSpot(null);
    setEntryTime('');
    setExitTime('');
    setSelectedDate(new Date());
    // No resetear selectedCar ni selectedParkingId para mejor UX
  };



  const calculateTotalPrice = () => {
    if (!currentParking || !entryTime || !exitTime) return 0;

    const [entryHour, entryMin] = entryTime.split(':').map(Number);
    const [exitHour, exitMin] = exitTime.split(':').map(Number);

    const entryInMinutes = entryHour * 60 + entryMin;
    const exitInMinutes = exitHour * 60 + exitMin;

    const durationInHours = Math.ceil((exitInMinutes - entryInMinutes) / 60);

    return durationInHours * currentParking.tarify;
  };
  const totalPrice = currentParking && selectedSpot && entryTime && exitTime ?
    calculateTotalPrice() : 0;

  // ========== RENDER ==========

  // Verificar que userVehicles sea un array antes de renderizar
  const safeUserVehicles = Array.isArray(userVehicles) ? userVehicles : [];

  if (isLoading && registeredParkings.length === 0) {
    return (
      <div className="reservas-main-container">
        <div className="reservas-no-parking-placeholder">
          <p className="reservas-no-parking-text">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reservas-main-container">
      <div className="reservas-content-grid">
        {/* Columna Izquierda - Formulario */}
        <div className="reservas-left-column">
          <h1 className="reservas-title">Reservar Parqueo</h1>

          {/* Selector de Auto */}
          <div className="reservas-form-group">
            <label className="reservas-form-label">
              <Car size={16} />
              Vehículo
            </label>
            <select
              value={selectedCar}
              onChange={(e) => setSelectedCar(e.target.value)}
              className="reservas-select"
              disabled={isLoading}
            >
              <option value="">Seleccione un vehículo</option>
              {safeUserVehicles.map((vehiclePlate, index) => (
                <option key={index} value={vehiclePlate}>
                  {vehiclePlate}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de Parqueo */}
          <div className="reservas-form-group">
            <label className="reservas-form-label">
              <MapPin size={16} />
              Parqueo
            </label>
            <select
              value={selectedParkingId}
              onChange={(e) => setSelectedParkingId(e.target.value)}
              className="reservas-select"
              disabled={isLoading}
            >
              <option value="">Seleccione un parqueo</option>
              {registeredParkings.map(parking => (
                <option key={parking._id} value={parking._id}>
                  {parking.parkingName} - ¢{parking.tarify}/hora
                </option>
              ))}
            </select>
          </div>

          {/* Calendario */}
                 <div className="ths-form-group">
                    <label className="ths-form-label">
                      <Calendar size={16} />
                      Día de ingreso
                    </label>
                    <div className="ths-calendar-wrapper">
                      <div className="ths-calendar-header">
                        <button
                          type="button"
                          onClick={() => changeMonth(-1)}
                          className="ths-calendar-nav-button"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <span className="ths-calendar-month-year">
                          {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                        </span>
                        <button
                          type="button"
                          onClick={() => changeMonth(1)}
                          className="ths-calendar-nav-button"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                      <div className="ths-calendar-grid">
                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                          <div key={day} className="ths-calendar-day-header">{day}</div>
                        ))}
                        {getDaysInMonth(selectedDate).map((day, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectDay(day)}
                            disabled={!day}
                            className={`ths-calendar-day-button ${day === selectedDate.getDate() ? 'selected' : ''
                              }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

          {/* Horarios */}
          <div className="reservas-time-container">
            <div className="reservas-form-group">
              <label className="reservas-form-label">
                <Clock size={16} />
                Hora de entrada
              </label>
              <input
                type="time"
                value={entryTime}
                onChange={(e) => setEntryTime(e.target.value)}
                className="reservas-time-input"
                disabled={isLoading}
              />
            </div>
            <div className="reservas-form-group">
              <label className="reservas-form-label">
                <Clock size={16} />
                Hora de salida
              </label>
              <input
                type="time"
                value={exitTime}
                onChange={(e) => setExitTime(e.target.value)}
                className="reservas-time-input"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Resumen de la Reserva */}
          {currentParking && selectedSpot && entryTime && exitTime && (
            <div className="reservas-price-info">
              <div className="reservas-price-breakdown">
                <h3>Resumen de Reserva</h3>
                <span><strong>Parqueo:</strong> {currentParking.parkingName}</span>
                <span><strong>Espacio:</strong> {selectedSpot}</span>
                <span><strong>Fecha:</strong> {selectedDate.toLocaleDateString('es-ES')}</span>
                <span><strong>Horario:</strong> {entryTime} - {exitTime}</span>
                <span><strong>Vehículo:</strong> {selectedCar}</span>
                <span><strong>Total estimado:</strong> ¢{totalPrice}</span>
              </div>
            </div>
          )}

          {/* Botón de Confirmación */}
          <button
            className={`reservas-action-button ${isLoading ? 'reservas-loading' : ''}`}
            onClick={handleCreateReservation}
            disabled={isLoading || !selectedSpot || !entryTime || !exitTime || !selectedCar || !selectedParkingId}
          >
            {isLoading ? 'Creando Reserva...' : 'Confirmar Reserva'}
          </button>
        </div>

        {/* Columna Derecha - Mapa del Parqueo */}
        <div className="reservas-right-column">
          <div className="reservas-parking-container">
            <h2 className="reservas-parking-title">Seleccionar Espacio</h2>
            {currentParking ? (
              <div className="reservas-parking-content">
                <div className="reservas-parking-map">
                  {currentParking.image && (
                    <img
                      ref={imageRef}
                      src={currentParking.image}
                      alt={`Layout de ${currentParking.parkingName}`}
                      className="reservas-parking-image"
                    />
                  )}

                  {/* Spots del parqueo */}
                  {currentParking.spots.map(spot => (
                    <button
                      key={spot.id}
                      onClick={() => toggleSpotSelection(spot.id)}
                      className={getSpotClass(spot)}
                      style={{
                        position: 'absolute',
                        left: `${spot.x}%`,
                        top: `${spot.y}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                      disabled={spot.status === 'occupied' || spot.status === 'reserved' || isLoading}
                      title={`Espacio ${spot.id} - ${spot.status}`}
                    >
                      {spot.id}
                    </button>
                  ))}
                </div>

                {/* Leyenda */}
                <div className="reservas-parking-legend">
                  <div className="reservas-legend-item">
                    <div className="reservas-legend-color-available"></div>
                    <span>Disponible</span>
                  </div>
                  <div className="reservas-legend-item">
                    <div className="reservas-legend-color-occupied"></div>
                    <span>Ocupado</span>
                  </div>
                  <div className="reservas-legend-item">
                    <div className="reservas-legend-color-reserved"></div>
                    <span>Reservado</span>
                  </div>
                  <div className="reservas-legend-item">
                    <div className="reservas-legend-color-selected"></div>
                    <span>Seleccionado</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="reservas-no-parking-placeholder">
                <MapPin size={48} className="reservas-no-parking-icon" />
                <p className="reservas-no-parking-text">Seleccione un parqueo para ver los espacios disponibles</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservaParqueo;